// docs/116: the inn's rows as the inn's layer and panels read them (`InnData`): the visit's seed, the
// dice table (with its actions, and the once-a-second tick that keeps it moving), the song asked for,
// the moments everyone sees, the guestbook, the World's deeds for the storyteller, the side quests and
// rumours for the noticeboard, and what can be paid for.
import { useEffect, useMemo, useRef } from 'react';
import { rpc } from '../../../../core/net/reducers';
import { identityHex, tsToMs } from '../../../../core/net/connection';
import { serverNow } from '../../../../core/time';
import { useInnCheers, useVillageMoments } from '../../../../core/inn/cheers';
import { useAdventureInstances, useChronicle, useDiceTables, useFoeFates, useInnGuestbook, useInnNotices, useInnRents, useInnSongs, useInnVisits, useOffers, useProfiles, useVillageNpcs, useWorldClock } from '../../../../core/subscriptions/hooks';
import { CLASS_NAMES } from '../../../../core/sim/adapt';
import { OFFER } from '../../../../core/adventure/graph';
import { DRINKS, MOMENTS, NOTE_PAPERS, type Deed, type DiceLast, type DiceSeatState, type DiceView, type DrinkKind, type GuestbookEntry, type InnCheer, type InnData, type InnMoment, type PlayerNotice } from './types';

const LAST: readonly DiceLast[] = ['none', 'thrown', 'pushed', 'bust', 'stood', 'gallows', 'crowns', 'hearth', 'triple', 'won', 'kitty', 'sat', 'left', 'started', 'timeout'];
const SEAT_STATE: readonly DiceSeatState[] = ['empty', 'waiting', 'playing', 'stood', 'bust', 'out'];
const STATE = ['open', 'playing', 'settling'] as const;
const KIND = ['empty', 'hero', 'regular'] as const;
const CHEER = ['toast', 'drink', 'song'] as const;
/** `regard.rs FATE_DEFEATED`. */
const FATE_DEFEATED = 1;
/** `inn.rs RENT_US`. */
const RENT_MS = 3 * 3_600_000;
/** The chronicle's deeds worth a tale (the storyteller's own lines for them). */
const TALE_KINDS = new Set(['AdventureSettled', 'PuzzleSolved', 'EncounterEnded', 'PromiseKept', 'LieExposed', 'KnotCut', 'SurrenderAccepted', 'FinalBlow']);

type Game = ReturnType<typeof useDiceTables>[number]['game'];

/** A `dice_table` row's game as the dice views draw it (my seat marked `mine`). */
export function diceViewOf(g: Game, me: string): DiceView {
  const dice = [...g.dice] as number[];
  return {
    state: STATE[g.state] ?? 'open',
    stake: g.stake, pot: g.pot, kitty: g.kitty,
    seats: g.seats.map((s) => {
      const kind = KIND[s.kind] ?? 'empty';
      const owner = kind === 'hero' ? identityHex(s.owner) : undefined;
      const hand = [...s.hand] as number[];
      return {
        kind, state: SEAT_STATE[s.state] ?? 'empty', name: s.name, frame: s.frame,
        ...(kind === 'hero' ? { classId: CLASS_NAMES[s.class] ?? 'fighter', heroId: s.heroId, owner } : {}),
        mine: kind === 'hero' && owner === me, score: s.score, hand: [hand[0] ?? 0, hand[1] ?? 0, hand[2] ?? 0],
        ...(kind === 'regular' ? { temper: s.temper } : {}), ...(s.won ? { won: s.won } : {}),
      };
    }),
    turn: g.turn,
    dice: [dice[0] ?? 0, dice[1] ?? 0, dice[2] ?? 0],
    thrown: g.thrown, throwN: g.throwN, pushes: g.pushes, last: LAST[g.last] ?? 'none',
    deadlineMs: Number(g.deadlineUs / 1000n), gameN: g.gameN, winners: [...g.winners],
  };
}

/** The table before anyone has ever sat at it (the module writes its row on the first `dice_sit`). */
const NO_GAME: DiceView = {
  state: 'open', stake: 0, pot: 0, kitty: 0,
  seats: [0, 1, 2, 3].map(() => ({ kind: 'empty', state: 'empty', name: '', frame: 0, mine: false, score: 0, hand: [0, 0, 0] })),
  turn: -1, dice: [0, 0, 0], thrown: 0, throwN: 0, pushes: 0, last: 'none', deadlineMs: 0, gameN: 0, winners: [],
};

interface Opts {
  worldId: bigint | undefined;
  villageId: bigint | undefined;
  me: string;
  /** In the inn or the room upstairs. */
  on: boolean;
  payers: Array<{ id: bigint; name: string; gold: number }>;
  /** My heroes in town, as the view keys them. */
  heroKeys: Map<bigint, string>;
  goUpstairs(up: boolean): void;
  /** docs/121 VW5: take up a side quest from the noticeboard. */
  takeUp?(offerId: bigint): Promise<boolean>;
}

export function useInnData({ worldId, villageId, me, on, payers, heroKeys, goUpstairs, takeUp }: Opts): InnData {
  const visits = useInnVisits(worldId);
  const tablesHere = useDiceTables(worldId);
  const songs = useInnSongs(worldId);
  const book = useInnGuestbook(worldId);
  const rents = useInnRents(worldId);
  const chronicle = useChronicle(worldId, 120);
  const fates = useFoeFates(worldId);
  const instances = useAdventureInstances(worldId);
  const offers = useOffers(worldId);
  const npcs = useVillageNpcs(worldId);
  const profiles = useProfiles();
  const clock = useWorldClock(worldId);
  const rawCheers = useInnCheers(worldId, villageId, on);
  const rawMoments = useVillageMoments(worldId, villageId, on);
  const pinned = useInnNotices(worldId);
  const nameOf = (hex: string) => profiles.find((p) => identityHex(p.identity) === hex)?.username || 'Someone';

  const row = tablesHere.find((t) => t.villageId === villageId);
  const dice = useMemo(() => (row ? diceViewOf(row.game, me) : NO_GAME), [row, me]);
  const vid = villageId ?? 0n;
  const diceActions = useMemo(() => ({
    sit: async (heroId: bigint) => (await rpc.diceSit(vid, heroId)).ok,
    stand: async () => (await rpc.diceStand(vid)).ok,
    start: async (stake: number) => (await rpc.diceStart(vid, stake)).ok,
    push: async (mask: number) => (await rpc.diceThrow(vid, mask)).ok,
    roll: async () => (await rpc.diceThrow(vid, 7)).ok,
    hold: async () => (await rpc.diceHold(vid)).ok,
    tick: () => void rpc.diceTick(vid),
  }), [vid]);
  // CI4: every client in the room nudges the table once something is due (the module acts only then)
  const live = useRef(dice);
  live.current = dice;
  useEffect(() => {
    if (!on) return;
    const t = window.setInterval(() => {
      const d = live.current;
      if (!d) return;
      const idle = d.state === 'open' && !d.seats.some((s) => s.kind === 'regular' && !d.seats.some((x) => x.kind === 'hero'));
      if (!idle && serverNow() >= d.deadlineMs) diceActions.tick();
    }, 1000);
    return () => window.clearInterval(t);
  }, [on, diceActions]);

  const song = songs.find((s) => s.villageId === villageId);
  const cheers: InnCheer[] = useMemo(() => rawCheers.map((c) => {
    const kind = CHEER[c.kind] ?? 'toast';
    const drink: DrinkKind | undefined = kind === 'drink' ? DRINKS[c.arg] : undefined;
    const heroKey = c.heroId ? heroKeys.get(c.heroId) ?? c.heroId.toString() : undefined;
    return { n: c.n, kind, owner: c.owner, who: c.owner === me ? 'You' : nameOf(c.owner), seed: c.seed, ...(heroKey ? { heroKey } : {}), ...(drink ? { drink } : {}), ...(kind === 'song' ? { tune: c.arg } : {}) };
  }), [rawCheers, profiles.length, heroKeys]);
  const moments: InnMoment[] = useMemo(() => rawMoments.flatMap((m) => {
    const kind = MOMENTS[m.kind];
    return kind ? [{ n: m.n, kind, heroKey: heroKeys.get(m.heroId) ?? m.heroId.toString(), x: m.x, y: m.y, seed: m.seed, atMs: m.atMs, mine: m.owner === me }] : [];
  }), [rawMoments, heroKeys, me]);
  const notices: PlayerNotice[] = useMemo(() => pinned.filter((e) => e.villageId === villageId).sort((a, b) => (a.id < b.id ? -1 : 1))
    .map((e) => { const hex = identityHex(e.owner); return { id: e.id, heroName: e.heroName, who: hex === me ? 'you' : nameOf(hex), title: e.title, text: e.text, paper: NOTE_PAPERS[e.paper] ?? 'parchment', atMs: tsToMs(e.at) ?? 0, mine: hex === me }; }), [pinned, villageId, profiles.length, me]);
  const guestbook: GuestbookEntry[] = useMemo(() => book.filter((e) => e.villageId === villageId).sort((a, b) => (a.id < b.id ? 1 : -1))
    .map((e) => { const hex = identityHex(e.owner); return { id: e.id, heroName: e.heroName, who: hex === me ? 'you' : nameOf(hex), text: e.text, atMs: (tsToMs(e.at) ?? 0), mine: hex === me }; }), [book, villageId, profiles.length]);
  // CI9: what the World's players did — the storyteller's own lines, named foes brought down, quests settled
  const deeds: Deed[] = useMemo(() => [
    ...chronicle.filter((e) => e.visibility === 0 && TALE_KINDS.has(e.kindName) && e.summary.trim()).slice(0, 24).map((e) => ({ kind: 'chronicle' as const, text: e.summary, atMs: (tsToMs(e.at) ?? 0) })),
    ...fates.filter((f) => f.fate === FATE_DEFEATED && f.name).map((f) => ({ kind: 'foe' as const, text: `${f.name} was brought down.`, name: f.name, atMs: 0 })),
    ...instances.filter((i) => i.settledAt && i.outcomeRow !== undefined && i.title).map((i) => ({ kind: 'quest' as const, text: `${i.title} was seen through.`, name: i.title, place: i.location, atMs: i.settledAt ? (tsToMs(i.settledAt) ?? 0) : 0 })),
  ], [chronicle, fates, instances]);
  const hooks = useMemo(() => offers.filter((o) => o.side && o.state === OFFER.OPEN).slice(0, 4)
    .map((o) => ({ title: o.title, asker: o.asker, pitch: o.pitch.replace('{asker}', o.asker), location: o.location, offerId: o.id })), [offers]);
  const rumours = useMemo(() => npcs.filter((n) => n.villageId === villageId).flatMap((n) => n.topics.filter((t) => !t.about && t.answer).slice(0, 1).map((t) => ({ who: n.name, text: t.answer }))).slice(0, 6), [npcs, villageId]);
  const rented = rents.some((r) => r.villageId === villageId && identityHex(r.owner) === me && serverNow() - (tsToMs(r.paidAt) ?? 0) < RENT_MS);

  return {
    visitSeed: visits.find((x) => x.villageId === villageId)?.seed ?? 0,
    ...(dice ? { dice, diceActions } : {}),
    ...(song ? { song: { tune: song.tune, startedAtMs: (tsToMs(song.startedAt) ?? 0), by: song.by } } : {}),
    cheers, moments, guestbook, notices, deeds, hooks, rumours,
    week: Number(clock?.weekIndex ?? 0n), seasonWeek: clock?.week ?? 1,
    payers: payers.map((h) => ({ id: h.id, key: heroKeys.get(h.id) ?? h.id.toString(), name: h.name, gold: h.gold })),
    rented,
    act: {
      toast: async () => (await rpc.innToast(vid)).ok,
      drink: async (heroId, drink) => (await rpc.orderDrink(vid, heroId, DRINKS.indexOf(drink))).ok,
      song: async (tune, heroId) => (await rpc.requestSong(vid, heroId, tune)).ok,
      sign: async (heroId, text) => (await rpc.signGuestbook(vid, heroId, text)).ok,
      rent: async (heroId) => (await rpc.rentRoom(vid, heroId)).ok,
      moment: async (kind, heroId, x, y) => (await rpc.shareMoment(vid, MOMENTS.indexOf(kind), heroId, x, y)).ok,
      pin: async (heroId, title, text, paper) => { const r = await rpc.pinNotice(vid, heroId, title, text, NOTE_PAPERS.indexOf(paper)); return r.ok ? true : r.error ?? 'The pin will not go in.'; },
      unpin: async (id) => (await rpc.unpinNotice(id)).ok,
      ...(takeUp ? { takeUp } : {}),
      upstairs: goUpstairs,
    },
  };
}
